'use client';

import { useEffect, useState, type FormEvent, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { useBlockPart, useCommerceSource, PortalLink } from '@akademi/site-builder-runtime';
import { AdapterPlaceholder, BlockRoot, sharedStyles } from '@akademi/site-builder-blocks';
import {
  AccountField,
  buttonStyle,
  errorStyle,
  formStyle,
  leadStyle,
  linkRowStyle,
  mutedStyle,
  noticeStyle,
  secondaryButtonStyle,
  signInHrefFor,
  titleStyle,
} from './accountShared';
import { AccountStep } from './TradeApplicationForm';
import { signUp } from './registration';
import {
  ActivationCodeStep,
  EMPTY_GUEST_ACCOUNT,
  GuestAccountFields,
  InstallerCertificatesUpload,
  guestAccountProblem,
  useGuestAccountProblemText,
  useSignUpRefusalText,
  type GuestAccountDraft,
} from './applyAtSignUp';
import {
  EMPTY_INSTALLER_DRAFT,
  EMPTY_POLICY_DRAFT,
  INSTALLER_FIELD_LIMITS,
  INSTALLER_POLICY_TYPES,
  INSTALLER_TRADES,
  MAX_CERTIFICATE_BYTES,
  formatCover,
  installerApplicationRequest,
  installerDraftProblem,
  installerErrorKind,
  policyRequired,
  readInstallerAreas,
  refusalMessage,
  type InstallerApplicationDraft,
  type InstallerApplicationStatus,
  type InstallerCommerceSource,
  type InstallerErrorKind,
  type InstallerPolicyType,
  type InstallerRequirements,
  type InstallerTrade,
} from './installer';
import {
  AgreementBox,
  InstallerSection,
  InstallerTextArea,
  PolicyFields,
  checkRowStyle,
  fieldStyle,
  inLanguage,
  megabytes,
  pairStyle,
  panelStyle,
  sectionTitleStyle,
  useInstallerProblemText,
  useInstallerWords,
  type Localized,
} from './installerParts';

const { sectionInner } = sharedStyles;

/**
 * Apply to join the shop's installers — "fitters" on a kitchen shop, by the `noun` prop — with
 * everything the shop will ask for laid out before the applicant starts.
 *
 * <p>An application belongs to an account, as a trade account application does: a visitor signs
 * in, or makes an account here, and comes back to the form. Then the business and how to reach
 * it, the trades they work in (from the list every shop shares), the postcode districts or areas
 * they cover — read here exactly as the shop reads them, so "LS1 to LS5" is refused in words
 * rather than quietly losing three districts — their registrations, whether they employ staff,
 * their insurance with a certificate for each policy and the shop's minimum cover beside it, and
 * the shop's published agreement, signed by typing their full name.
 *
 * <p>A shop that takes no applications shows a sentence saying so; one that has not published an
 * agreement yet shows what it will ask for and no form, since nothing can be signed.
 */
export interface InstallerApplicationFormProps {
  title?: Localized;
  intro?: Localized;
  /** What the shop calls its installers, e.g. "fitter". Defaults to "installer". */
  noun?: Localized;
  /** The plural, e.g. "fitters". Defaults to the noun with an "s". */
  nounPlural?: Localized;
  signInHref?: string;
  accountHref?: string;
  termsHref?: string;
}

type Viewer = { email: string; name: string } | null;

type Phase =
  | { kind: 'checking' }
  | { kind: 'off' }
  | { kind: 'failed' }
  | { kind: 'ready'; viewer: Viewer; requirements: InstallerRequirements };

/** What follows a guest's application sent with their sign-up; see the trade form's. */
type AfterSignUp =
  | { kind: 'form' }
  | { kind: 'activate'; email: string; password: string }
  | { kind: 'certificates'; types: InstallerPolicyType[]; email: string }
  | { kind: 'signIn' };

export function InstallerApplicationForm({
  title,
  intro,
  noun,
  nounPlural,
  signInHref = '/sign-in',
  accountHref = '/account',
  termsHref = '/terms',
}: InstallerApplicationFormProps) {
  const commerce = useCommerceSource() as InstallerCommerceSource | null;
  const part = useBlockPart();
  const { t, i18n } = useTranslation('site-builder-blocks');
  const words = useInstallerWords(noun, nounPlural);
  const problemText = useInstallerProblemText(words);
  const language = i18n?.language;

  const [phase, setPhase] = useState<Phase>({ kind: 'checking' });
  const [draft, setDraft] = useState<InstallerApplicationDraft>(EMPTY_INSTALLER_DRAFT);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<{ text: string; kind?: InstallerErrorKind } | null>(null);
  const [sent, setSent] = useState<InstallerApplicationStatus | null>(null);
  const [here, setHere] = useState<string | null>(null);
  const [guest, setGuest] = useState<GuestAccountDraft>(EMPTY_GUEST_ACCOUNT);
  const [after, setAfter] = useState<AfterSignUp>({ kind: 'form' });
  const guestProblemText = useGuestAccountProblemText();
  const signUpRefusalText = useSignUpRefusalText();

  useEffect(() => {
    let cancelled = false;
    setHere(window.location.pathname + window.location.search);
    const source = commerce;
    if (!source?.submitInstallerApplication || !source.installerApplicationRequirements) return;
    void (async () => {
      let requirements: InstallerRequirements;
      try {
        requirements = await source.installerApplicationRequirements!();
      } catch (failure) {
        if (!cancelled) setPhase({ kind: installerErrorKind(failure) === 'NOT_FOUND' ? 'off' : 'failed' });
        return;
      }
      const profile = await (source.accountProfile?.().catch(() => null) ?? Promise.resolve(null));
      if (cancelled) return;
      const viewer = profile
        ? { email: profile.email, name: [profile.firstName, profile.lastName].filter(Boolean).join(' ') }
        : null;
      if (viewer) {
        setDraft((current) => ({
          ...current,
          email: viewer.email,
          contactName: current.contactName || viewer.name,
          fullName: current.fullName || viewer.name,
        }));
      }
      setPhase({ kind: 'ready', viewer, requirements: { agreement: requirements?.agreement ?? null, minimumCover: requirements?.minimumCover ?? {} } });
    })();
    return () => {
      cancelled = true;
    };
  }, [commerce]);

  if (!commerce?.submitInstallerApplication || !commerce.installerApplicationRequirements) {
    return (
      <BlockRoot as="section">
        <div style={sectionInner}>
          <AdapterPlaceholder
            blockType="InstallerApplicationForm"
            adapterName="CommerceSource"
            reason={t('commerce.installer.noAdapter', { ...words })}
          />
        </div>
      </BlockRoot>
    );
  }

  const viewer = phase.kind === 'ready' ? phase.viewer : null;
  const requirements: InstallerRequirements =
    phase.kind === 'ready' ? phase.requirements : { agreement: null, minimumCover: {} };
  const minimum = (type: InstallerPolicyType) => requirements.minimumCover[type];
  const amount = (type: InstallerPolicyType) => {
    const value = minimum(type);
    return value ? formatCover(value, language) : null;
  };
  const areasReading = draft.areas.trim() ? readInstallerAreas(draft.areas) : null;

  const set =
    (field: Exclude<keyof InstallerApplicationDraft, 'trades' | 'employsStaff' | 'policies' | 'agreed'>) =>
    (value: string) =>
      setDraft((current) => ({ ...current, [field]: value }));

  function toggleTrade(trade: InstallerTrade, on: boolean) {
    setDraft((current) => ({
      ...current,
      trades: on ? [...current.trades, trade] : current.trades.filter((item) => item !== trade),
    }));
  }

  function setEmploysStaff(on: boolean) {
    setDraft((current) => ({
      ...current,
      employsStaff: on,
      policies:
        on && !current.policies.EMPLOYERS_LIABILITY
          ? { ...current.policies, EMPLOYERS_LIABILITY: { ...EMPTY_POLICY_DRAFT } }
          : current.policies,
    }));
  }

  function setPolicy(type: InstallerPolicyType, value: InstallerApplicationDraft['policies'][InstallerPolicyType] | null) {
    setDraft((current) => {
      const policies = { ...current.policies };
      if (value) policies[type] = value;
      else delete policies[type];
      return { ...current, policies };
    });
  }

  /** The requirements again, after the shop said the agreement changed or is gone. */
  async function reloadRequirements() {
    try {
      const fresh = await commerce!.installerApplicationRequirements!();
      setPhase((current) =>
        current.kind === 'ready'
          ? { ...current, requirements: { agreement: fresh?.agreement ?? null, minimumCover: fresh?.minimumCover ?? {} } }
          : current,
      );
    } catch {
      // The sentence already shown asks them to try again; the form stays as it was.
    }
  }

  async function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!viewer) {
      await submitWithSignUp();
      return;
    }
    if (!commerce?.submitInstallerApplication || busy || phase.kind !== 'ready') return;
    const problem = installerDraftProblem(draft, { requirements });
    if (problem || !requirements.agreement) {
      setError({ text: problemText(problem ?? { kind: 'NO_AGREEMENT' }) });
      return;
    }
    setBusy(true);
    setError(null);
    try {
      const { request, certificates } = installerApplicationRequest(draft, {
        agreement: requirements.agreement,
        accountEmail: viewer.email,
        language,
      });
      setSent(await commerce.submitInstallerApplication(request, certificates));
    } catch (failure) {
      const kind = installerErrorKind(failure);
      if (kind === 'NOT_FOUND') {
        setPhase({ kind: 'off' });
      } else if (kind === 'AGREEMENT_CHANGED' || kind === 'NO_AGREEMENT') {
        // What they signed is not what is published now: show the current text, and ask again.
        setDraft((current) => ({ ...current, agreed: false }));
        setError({ kind, text: t(`commerce.installer.application.errors.${kind}`, { ...words }) });
        await reloadRequirements();
      } else if (kind === 'INVALID_INPUT' || kind === 'INVALID_FILE') {
        // The shop's own sentence names the field; it is written for the applicant.
        setError({ kind, text: refusalMessage(failure) ?? t(`commerce.installer.application.errors.${kind}`, { ...words }) });
      } else {
        setError({ kind, text: t(`commerce.installer.application.errors.${kind}`, { ...words }) });
      }
    } finally {
      setBusy(false);
    }
  }

  /**
   * A guest's application, in one step with their account (contract §7). A sign-up cannot carry
   * the certificates, so they are asked for once the shopper is signed in; the files chosen on the
   * form are ready there.
   */
  async function submitWithSignUp() {
    if (!commerce?.register || busy || phase.kind !== 'ready') return;
    const accountProblem = guestAccountProblem(guest);
    if (accountProblem) {
      setError({ text: guestProblemText(accountProblem) });
      return;
    }
    const problem = installerDraftProblem(draft, { requirements, certificatesLater: true });
    if (problem || !requirements.agreement) {
      setError({ text: problemText(problem ?? { kind: 'NO_AGREEMENT' }) });
      return;
    }
    const email = guest.email.trim().toLowerCase();
    setBusy(true);
    setError(null);
    try {
      const { request } = installerApplicationRequest(draft, {
        agreement: requirements.agreement,
        accountEmail: email,
        language,
      });
      const outcome = await signUp(commerce, {
        email,
        firstName: guest.firstName.trim(),
        lastName: guest.lastName.trim(),
        password: guest.password,
        confirmPassword: guest.confirmPassword,
        accountType: 'INSTALLER',
        application: request,
      });
      if (outcome.signedIn) await afterSignedIn(email);
      else if (outcome.activationRequired) setAfter({ kind: 'activate', email, password: guest.password });
      else setAfter({ kind: 'signIn' });
    } catch (failure) {
      setError({ text: signUpRefusalText(failure) });
      if (failure instanceof Error && (failure as { kind?: string }).kind === 'APPLICATION_REFUSED') {
        // The agreement may have changed while they read it (the sentence then says so): show the
        // current text, and ask for the box again only when it is a different one.
        const signed = requirements.agreement;
        try {
          const fresh = await commerce.installerApplicationRequirements!();
          const agreement = fresh?.agreement ?? null;
          if (agreement?.version !== signed?.version || agreement?.textHash !== signed?.textHash) {
            setDraft((current) => ({ ...current, agreed: false }));
            setPhase((current) =>
              current.kind === 'ready'
                ? { ...current, requirements: { agreement, minimumCover: fresh?.minimumCover ?? {} } }
                : current,
            );
          }
        } catch {
          // The sentence already shown is what they need; the form stays as it was.
        }
      }
    } finally {
      setBusy(false);
    }
  }

  /** The policies on the application, in the shop's order. */
  function policyTypes(): InstallerPolicyType[] {
    return INSTALLER_POLICY_TYPES.filter((type) => !!draft.policies[type]);
  }

  /** The certificate files chosen on the form, by policy type. */
  function chosenCertificates(): Partial<Record<InstallerPolicyType, File>> {
    const files: Partial<Record<InstallerPolicyType, File>> = {};
    for (const type of policyTypes()) {
      const file = draft.policies[type]?.file;
      if (file) files[type] = file;
    }
    return files;
  }

  /** Signed in after applying: the certificates step, for what the shop says is still outstanding. */
  async function afterSignedIn(email: string) {
    let outstanding: InstallerPolicyType[] | null = null;
    try {
      outstanding = (await commerce?.installerMe?.())?.certificatesOutstanding ?? [];
    } catch {
      // Where it stands cannot be read just now: ask for a certificate for every policy given.
    }
    const types = (outstanding ?? policyTypes()).filter((type) => policyTypes().includes(type));
    if (commerce?.uploadInstallerCertificates && types.length) {
      setAfter({ kind: 'certificates', types, email });
      return;
    }
    setSent(pendingStatus(email));
  }

  function pendingStatus(email: string): InstallerApplicationStatus {
    return { status: 'PENDING', businessName: draft.businessName.trim(), email, submittedAt: new Date().toISOString() };
  }

  const optional = (label: string) => `${label} ${t('commerce.installer.application.optional')}`;
  const field = (name: string) => t(`commerce.installer.application.fields.${name}`);

  const heading = (
    <h1 {...part('title')} style={titleStyle}>
      {inLanguage(title, language) || t('commerce.installer.application.title', { ...words })}
    </h1>
  );
  const introText = (
    <p {...part('intro')} style={leadStyle}>
      {inLanguage(intro, language) || t('commerce.installer.application.intro', { ...words })}
    </p>
  );
  const accountLink = (
    <div style={linkRowStyle}>
      <PortalLink href={accountHref}>{t('commerce.installer.application.goToAccount')}</PortalLink>
    </div>
  );

  const checklist = (
    <div {...part('checklist')} style={{ ...panelStyle, marginBottom: 24 }}>
      <h2 style={sectionTitleStyle}>{t('commerce.installer.application.checklist.title')}</h2>
      <ul style={{ margin: 0, paddingLeft: 20, display: 'grid', gap: 4 }}>
        <li>{t('commerce.installer.application.checklist.business')}</li>
        <li>{t('commerce.installer.application.checklist.trades')}</li>
        <li>{t('commerce.installer.application.checklist.areas')}</li>
        <li>{t('commerce.installer.application.checklist.registrations')}</li>
        <li>
          {amount('PUBLIC_LIABILITY')
            ? t('commerce.installer.application.checklist.publicLiabilityMinimum', { amount: amount('PUBLIC_LIABILITY') })
            : t('commerce.installer.application.checklist.publicLiability')}
        </li>
        <li>
          {amount('EMPLOYERS_LIABILITY')
            ? t('commerce.installer.application.checklist.employersMinimum', { amount: amount('EMPLOYERS_LIABILITY') })
            : t('commerce.installer.application.checklist.employers')}
        </li>
        <li>{t('commerce.installer.application.checklist.optionalPolicies')}</li>
        <li>{t('commerce.installer.application.checklist.files', { size: megabytes(MAX_CERTIFICATE_BYTES) })}</li>
        <li>{t('commerce.installer.application.checklist.agreement', { ...words })}</li>
      </ul>
      <p style={{ ...mutedStyle, margin: '8px 0 0', fontSize: 14 }}>
        {t('commerce.installer.application.checklist.review')}
      </p>
    </div>
  );

  let body: ReactNode;
  if (phase.kind === 'checking') {
    body = <p style={mutedStyle}>{t('commerce.installer.application.loading')}</p>;
  } else if (phase.kind === 'off') {
    body = (
      <p role="status" style={mutedStyle}>
        {t('commerce.installer.application.unavailable', { ...words })}
      </p>
    );
  } else if (phase.kind === 'failed') {
    body = (
      <p role="alert" style={errorStyle}>
        {t('commerce.installer.application.failed')}
      </p>
    );
  } else if (sent) {
    body = (
      <>
        <p role="status" style={noticeStyle}>
          {t('commerce.installer.application.sent', { email: sent.email, ...words })}
        </p>
        {accountLink}
      </>
    );
  } else if (after.kind === 'activate') {
    body = (
      <ActivationCodeStep
        commerce={commerce}
        email={after.email}
        password={after.password}
        idPrefix="installer"
        signInHref={signInHref}
        here={here}
        onSignedIn={() => afterSignedIn(after.email)}
      />
    );
  } else if (after.kind === 'certificates') {
    const { email } = after;
    body = (
      <InstallerCertificatesUpload
        commerce={commerce}
        types={after.types}
        initial={chosenCertificates()}
        onDone={(status) => setSent({ ...pendingStatus(email), ...status })}
      />
    );
  } else if (after.kind === 'signIn') {
    body = (
      <>
        <p role="status" style={noticeStyle}>
          {t('commerce.signUpApply.activate.readySignIn')}
        </p>
        <div style={linkRowStyle}>
          <PortalLink href={signInHrefFor(signInHref, accountHref)}>{t('commerce.account.signIn')}</PortalLink>
        </div>
      </>
    );
  } else if (!requirements.agreement) {
    body = (
      <>
        {introText}
        {checklist}
        <p role="status" style={mutedStyle}>
          {t('commerce.installer.application.noAgreement', { ...words })}
        </p>
      </>
    );
  } else if (!viewer && !commerce.register) {
    // A host that cannot make accounts: sign in, and come back to the form.
    body = (
      <>
        {introText}
        {checklist}
        <AccountStep
          commerce={commerce}
          signInHref={signInHref}
          termsHref={termsHref}
          here={here}
          idPrefix="installer"
          lead={t('commerce.installer.application.account.lead')}
          confirmEmail={(email) => t('commerce.installer.application.account.confirmEmail', { email })}
        />
      </>
    );
  } else {
    const agreement = requirements.agreement;
    body = (
      <>
        {introText}
        {checklist}
        {/* The browser's own checks are off: they would refuse a website typed without https://.
            Required fields are checked in submit() instead, in the shop's words. */}
        <form onSubmit={(event) => void submit(event)} noValidate style={{ ...formStyle, maxWidth: 600, gap: 28 }}>
          {!viewer && (
            <GuestAccountFields value={guest} onChange={setGuest} idPrefix="installer" signInHref={signInHref} here={here} />
          )}
          <InstallerSection title={t('commerce.installer.application.sections.business')}>
            <AccountField
              id="installer-business-name"
              label={field('businessName')}
              value={draft.businessName}
              onChange={set('businessName')}
              autoComplete="organization"
              required
              maxLength={INSTALLER_FIELD_LIMITS.businessName}
            />
            <AccountField
              id="installer-contact-name"
              label={field('contactName')}
              value={draft.contactName}
              onChange={set('contactName')}
              autoComplete="name"
              required
              maxLength={INSTALLER_FIELD_LIMITS.contactName}
            />
            <AccountField
              id="installer-email"
              label={field('email')}
              value={viewer ? draft.email : guest.email.trim().toLowerCase()}
              onChange={set('email')}
              type="email"
              readOnly
              hint={t(viewer ? 'commerce.installer.application.emailFromAccount' : 'commerce.signUpApply.emailFromAbove')}
            />
            <AccountField
              id="installer-phone"
              label={field('phone')}
              value={draft.phone}
              onChange={set('phone')}
              type="tel"
              autoComplete="tel"
              required
              maxLength={INSTALLER_FIELD_LIMITS.phone}
            />
            <div style={pairStyle}>
              <AccountField
                id="installer-company-number"
                label={optional(field('companyNumber'))}
                value={draft.companyNumber}
                onChange={set('companyNumber')}
                maxLength={INSTALLER_FIELD_LIMITS.number}
              />
              <AccountField
                id="installer-vat-number"
                label={optional(field('vatNumber'))}
                value={draft.vatNumber}
                onChange={set('vatNumber')}
                maxLength={INSTALLER_FIELD_LIMITS.number}
              />
            </div>
            <InstallerTextArea
              id="installer-business-address"
              label={optional(field('businessAddress'))}
              value={draft.businessAddress}
              onChange={set('businessAddress')}
              rows={3}
              maxLength={INSTALLER_FIELD_LIMITS.businessAddress}
            />
            <AccountField
              id="installer-website"
              label={optional(field('website'))}
              value={draft.website}
              onChange={set('website')}
              type="url"
              autoComplete="url"
              maxLength={INSTALLER_FIELD_LIMITS.website}
            />
          </InstallerSection>

          <InstallerSection
            title={t('commerce.installer.application.sections.trades')}
            note={t('commerce.installer.application.tradesHint')}
          >
            <div style={tradesGridStyle}>
              {INSTALLER_TRADES.map((trade) => (
                <label key={trade} style={checkRowStyle}>
                  <input
                    type="checkbox"
                    name="installer-trades"
                    value={trade}
                    checked={draft.trades.includes(trade)}
                    onChange={(event) => toggleTrade(trade, event.target.checked)}
                  />
                  <span>{t(`commerce.installer.trades.${trade}`)}</span>
                </label>
              ))}
            </div>
            {draft.trades.includes('OTHER') && (
              <AccountField
                id="installer-other-trade"
                label={field('otherTrade')}
                value={draft.otherTrade}
                onChange={set('otherTrade')}
                required
                maxLength={INSTALLER_FIELD_LIMITS.otherTrade}
              />
            )}
          </InstallerSection>

          <InstallerSection title={t('commerce.installer.application.sections.areas')}>
            <InstallerTextArea
              id="installer-areas"
              label={field('areas')}
              value={draft.areas}
              onChange={set('areas')}
              rows={3}
              hint={t('commerce.installer.application.areasHint')}
            />
            {areasReading?.ok && (
              <p style={{ ...mutedStyle, fontSize: 14 }} aria-live="polite">
                {t('commerce.installer.application.areasRead', { areas: areasReading.ok ? areasReading.areas.join(', ') : '' })}
              </p>
            )}
            {areasReading && !areasReading.ok && (
              <p style={{ ...errorStyle, fontSize: 14, padding: '8px 12px' }} aria-live="polite">
                {problemText({ kind: 'AREAS', reading: areasReading })}
              </p>
            )}
          </InstallerSection>

          <InstallerSection
            title={t('commerce.installer.application.sections.registrations')}
            note={t('commerce.installer.application.registrationsHint')}
          >
            <div style={pairStyle}>
              {(['gasSafe', 'niceic', 'napit', 'mcs'] as const).map((name) => (
                <AccountField
                  key={name}
                  id={`installer-${name.toLowerCase()}`}
                  label={optional(field(name))}
                  value={draft[name]}
                  onChange={set(name)}
                  maxLength={INSTALLER_FIELD_LIMITS.number}
                />
              ))}
            </div>
            <div style={pairStyle}>
              <AccountField
                id="installer-other-scheme"
                label={optional(field('otherName'))}
                value={draft.otherName}
                onChange={set('otherName')}
                maxLength={INSTALLER_FIELD_LIMITS.schemeName}
              />
              <AccountField
                id="installer-other-number"
                label={optional(field('otherNumber'))}
                value={draft.otherNumber}
                onChange={set('otherNumber')}
                maxLength={INSTALLER_FIELD_LIMITS.number}
              />
            </div>
          </InstallerSection>

          <InstallerSection
            title={t('commerce.installer.application.sections.insurance')}
            note={
              viewer
                ? t('commerce.installer.application.insuranceNote')
                : `${t('commerce.installer.application.insuranceNote')} ${t('commerce.signUpApply.installer.certificatesLater')}`
            }
          >
            <label style={checkRowStyle}>
              <input
                type="checkbox"
                checked={draft.employsStaff}
                onChange={(event) => setEmploysStaff(event.target.checked)}
              />
              <span>
                {t('commerce.installer.application.employsStaff')}
                <span style={{ ...mutedStyle, display: 'block', fontSize: 13 }}>
                  {t('commerce.installer.application.employsStaffHint')}
                </span>
              </span>
            </label>
            {INSTALLER_POLICY_TYPES.map((type) => {
              const required = policyRequired(type, draft.employsStaff);
              const policy = draft.policies[type];
              const name = t(`commerce.installer.policies.${type}`);
              if (!policy) {
                return (
                  <button
                    key={type}
                    type="button"
                    style={{ ...secondaryButtonStyle, justifySelf: 'start' }}
                    onClick={() => setPolicy(type, { ...EMPTY_POLICY_DRAFT })}
                  >
                    {t('commerce.installer.application.addPolicy', { policy: name })}
                  </button>
                );
              }
              return (
                <PolicyFields
                  key={type}
                  idPrefix="installer"
                  type={type}
                  value={policy}
                  onChange={(value) => setPolicy(type, value)}
                  minimum={minimum(type)}
                  legend={`${name} — ${t(required ? 'commerce.installer.policy.required' : 'commerce.installer.policy.optional')}`}
                >
                  {!required && (
                    <button
                      type="button"
                      style={{ ...secondaryButtonStyle, justifySelf: 'start' }}
                      onClick={() => setPolicy(type, null)}
                    >
                      {t('commerce.installer.application.removePolicy', { policy: name })}
                    </button>
                  )}
                </PolicyFields>
              );
            })}
          </InstallerSection>

          <InstallerSection title={t('commerce.installer.application.sections.message')}>
            <InstallerTextArea
              id="installer-message"
              label={optional(field('message'))}
              value={draft.message}
              onChange={set('message')}
              rows={4}
              maxLength={INSTALLER_FIELD_LIMITS.message}
            />
          </InstallerSection>

          <InstallerSection title={t('commerce.installer.application.sections.agreement')}>
            <AgreementBox
              idPrefix="installer"
              agreement={agreement}
              fullName={draft.fullName}
              agreed={draft.agreed}
              onFullName={set('fullName')}
              onAgreed={(agreed) => setDraft((current) => ({ ...current, agreed }))}
            />
          </InstallerSection>

          {error && (
            <div role="alert" style={errorStyle}>
              {error.text}
              {(error.kind === 'ALREADY_INSTALLER' || error.kind === 'APPLICATION_OPEN') && (
                <>
                  {' '}
                  <PortalLink href={accountHref}>{t('commerce.installer.application.goToAccount')}</PortalLink>
                </>
              )}
            </div>
          )}
          <button type="submit" disabled={busy} style={buttonStyle}>
            {busy
              ? t('commerce.installer.application.submitting')
              : viewer
                ? t('commerce.installer.application.submit')
                : t('commerce.signUpApply.submit')}
          </button>
        </form>
      </>
    );
  }

  return (
    <BlockRoot as="section">
      <div style={{ ...sectionInner, maxWidth: 720 }}>
        {heading}
        {body}
      </div>
    </BlockRoot>
  );
}

const tradesGridStyle = { ...fieldStyle, gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))', gap: 8 };
